import Add20 from '@carbon/icons-react/es/add/20';
import ArrowRight20 from '@carbon/icons-react/es/arrow--right/20';
import Link20 from '@carbon/icons-react/es/link/20';
import Search20 from '@carbon/icons-react/es/search/20';
import { useBoolean } from 'ahooks';
import { Form, FormInstance, Image, Space } from 'antd';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { ThemeProvider } from 'styled-components';
import { UserType } from 'api/models';
import { Loading } from 'components/atoms/loading';
import { SpaceTabEmptyState } from 'components/organisms/spaces/space-tab/new-tab/empty-state';
import { HelperText01, Text03 } from 'components/styles';
import { grayTheme } from 'components/styles/theme';
import { Template } from 'data/templates/api';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { DocumentTemplateList } from './document-template-list';
import {
  Box,
  EmbedWrapper,
  Heading,
  Input,
  StyledForm,
  StyledFormItem,
  SubmitButton,
} from './styles';

const SupportedServices = {
  googleDocument:
    'https://ssl.gstatic.com/docs/doclist/images/mediatype/icon_1_document_x32.png',
  googleSheet:
    'https://ssl.gstatic.com/docs/doclist/images/mediatype/icon_1_spreadsheet_x32.png',
  googlePresentation:
    'https://ssl.gstatic.com/docs/doclist/images/mediatype/icon_1_presentation_x32.png',
  msWord:
    'https://ssl.gstatic.com/docs/doclist/images/mediatype/icon_1_word_x32.png',
  msExcel:
    'https://ssl.gstatic.com/docs/doclist/images/mediatype/icon_1_excel_x32.png',
  msPowerPoint:
    'https://ssl.gstatic.com/docs/doclist/images/mediatype/icon_1_powerpoint_x32.png',
  figma: 'https://static.figma.com/app/icon/1/favicon.png',
};

const NewDocumentLoading = styled(Loading)`
  --stroke-color: var(--white-100);
  circle {
    stroke: var(--stroke-color);
  }
`;

const Help = styled.div`
  ${HelperText01};
  ${Text03};
  margin-top: var(--spacing-03);
`;
const HelpInvertedColor = styled(Help)`
  color: var(--white-70);
`;

const StyledTabContent = styled.div`
  padding: var(--spacing-10) var(--spacing-07);
  height: 100%;
`;

export const SpaceNewTabPaneContent: FC<{
  isCreateInProgress?: boolean;
  isEmbedInProgress?: boolean;
  isCreateFromTemplateInProgress?: boolean;
  onCreateDocument(): void;
  onCreateEmbed(values: any, form: FormInstance): void;
  onChooseTemplate?(template: Template): void;
}> = ({
  isCreateInProgress,
  isEmbedInProgress,
  isCreateFromTemplateInProgress = false,
  onCreateDocument,
  onCreateEmbed,
  onChooseTemplate,
}) => {
  const placeholder = useTranslation('space_tab.embed.input.placeholder');
  const label = useTranslation('space_tab.embed.input.label');
  const errorMessage = useTranslation('input.required');
  const urlErrorMessage = useTranslation('input.url');
  const { user } = useUser();

  const [form] = Form.useForm();

  const [canShowEmbedForm, { setTrue: showEmbedForm }] = useBoolean(false);

  const handleFinish = (values) => {
    onCreateEmbed(values, form);
  };

  const [showTemplateSearchModal, setShowTemplateSearchModal] = useState(false);

  const onChooseFromTemplate = () => {
    setShowTemplateSearchModal(true);
  };

  const onCloseTemplateSearchModal = () => setShowTemplateSearchModal(false);

  return (
    <StyledTabContent>
      <Space size={36} direction="vertical">
        <div>
          <Heading>
            <FormattedMessage id="space_tab.new.heading" />
          </Heading>
          <Space size={24}>
            <Box
              data-preview-disable="true"
              active
              role="button"
              onClick={() => !isCreateInProgress && onCreateDocument()}
              data-test-id="space_tab.blank.document"
            >
              {isCreateInProgress ? (
                <NewDocumentLoading size="small" />
              ) : (
                <Add20 />
              )}
              <div>
                <FormattedMessage id="space_tab.blank.document" />
                <HelpInvertedColor>
                  <FormattedMessage id="space_tab.blank.document.help" />
                </HelpInvertedColor>
              </div>
            </Box>
            <Box onClick={showEmbedForm} data-test-id="space_tab.embed.url">
              <Link20 />
              <div>
                <FormattedMessage id="space_tab.blank.embed" />
                <Help>
                  <FormattedMessage id="space_tab.blank.embed.help" />
                </Help>
              </div>
            </Box>
            {user.userType !== UserType.External && (
              <Box
                role="button"
                onClick={() =>
                  !isCreateFromTemplateInProgress && onChooseFromTemplate()
                }
                data-test-id="space_tab.use.template"
              >
                {isCreateFromTemplateInProgress ? (
                  <Loading size="small" />
                ) : (
                  <Search20 />
                )}
                <div>
                  <FormattedMessage id="space_tab.blank.template" />
                  <Help>
                    <FormattedMessage id="space_tab.blank.template.help" />
                  </Help>
                </div>
              </Box>
            )}
          </Space>
        </div>
        {canShowEmbedForm ? (
          <div>
            <Heading>
              <FormattedMessage id="space_tab.embed.heading" />
            </Heading>
            <EmbedWrapper>
              <Space size={12}>
                {Object.values(SupportedServices).map((url) => (
                  <Image preview={false} key={url} width={24} src={url} />
                ))}
              </Space>
              <ThemeProvider theme={grayTheme}>
                <StyledForm
                  form={form}
                  onFinish={handleFinish}
                  layout="inline"
                  name="new-embed"
                  initialValues={{ url: '' }}
                >
                  <StyledFormItem
                    hasFeedback
                    label={label}
                    name="url"
                    rules={[
                      {
                        required: true,
                        message: errorMessage,
                      },
                      {
                        pattern: /^https?:\/\//i,
                        message: urlErrorMessage,
                      },
                    ]}
                  >
                    <Input
                      autoFocus
                      type="url"
                      name="url"
                      placeholder={placeholder}
                      data-cy="space_tab.embed_url.input"
                    />
                  </StyledFormItem>
                  <Form.Item key="submit">
                    <SubmitButton
                      disabled={isCreateInProgress}
                      loading={isEmbedInProgress}
                      icon={<ArrowRight20 />}
                      htmlType="submit"
                      data-cy="space_tab.embed_url.submit_button"
                    >
                      <FormattedMessage id="space_tab.embed" />
                    </SubmitButton>
                  </Form.Item>
                </StyledForm>
              </ThemeProvider>
            </EmbedWrapper>
          </div>
        ) : null}

        <SpaceTabEmptyState />
        <DocumentTemplateList
          visible={showTemplateSearchModal}
          onClose={onCloseTemplateSearchModal}
          onChooseTemplate={(template) => {
            if (onChooseTemplate) {
              onChooseTemplate(template);
            }

            onCloseTemplateSearchModal();
          }}
        />
      </Space>
    </StyledTabContent>
  );
};
